import { useState, useEffect } from 'react'
import {
  Bell,
  Check,
  X,
  AlertTriangle,
  Lock,
  Smartphone,
} from 'lucide-react'
import {
  getNotificationPreferences,
  updateNotificationPreferences,
  type NotificationPreference,
} from '../lib/api'
import { isIOS } from '../lib/pwaUtils'
import { useUser } from '../hooks/useUser'
import { usePushSubscription } from '../hooks/usePushSubscription'
import { Switch } from './ui/Switch'
import {
  PLATFORM_NOTIFICATION_TYPES,
  USER_NOTIFICATION_TYPES,
  CLAUDE_NOTIFICATION_TYPES,
  ADMIN_NOTIFICATION_TYPES,
  ADMIN_MONITORING_TYPES,
} from '../lib/notificationCatalog'

/**
 * Notification Preferences modal — single source of truth for the panel
 * shown from both the Notifications page and the bell popover. Catalog
 * (PLATFORM/USER/CLAUDE/ADMIN/MONITORING) is imported from notificationCatalog.ts
 * which Settings.tsx also consumes, so a toggle here changes the same row
 * the Settings page renders.
 */
export function NotificationPreferencesModal({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const user = useUser()
  const {
    isSupported: pushSupported,
    isSubscribed: pushEnabled,
    isLoading: pushLoading,
    permission: pushPermission,
    subscribe: enablePush,
    unsubscribe: disablePush,
    error: pushError,
    needsInstallFirst: pushNeedsInstall,
  } = usePushSubscription()

  const [notifPrefs, setNotifPrefs] = useState<Record<string, boolean>>({})
  const [notifSaving, setNotifSaving] = useState(false)
  const [notifSaved, setNotifSaved] = useState(false)

  useEffect(() => {
    if (!open) return
    getNotificationPreferences().then(prefs => {
      const map: Record<string, boolean> = {}
      prefs.forEach((p: NotificationPreference) => { map[p.type] = p.enabled })
      setNotifPrefs(map)
    }).catch(() => {})
  }, [open])

  async function handleNotifToggle(type: string, enabled: boolean) {
    const updated = { ...notifPrefs, [type]: enabled }
    setNotifPrefs(updated)
    setNotifSaving(true)
    setNotifSaved(false)
    try {
      const prefs = Object.entries(updated).map(([t, e]) => ({ type: t, enabled: e }))
      await updateNotificationPreferences(prefs)
      setNotifSaved(true)
      setTimeout(() => setNotifSaved(false), 2000)
    } catch {
      setNotifPrefs(notifPrefs)
    } finally {
      setNotifSaving(false)
    }
  }

  if (!open || !user) return null

  return (
    // On mobile we top-anchor below the sticky page header (iPhone safe-area + ~3rem).
    // Centering would let the modal extend up into the header band and clip its top.
    // Desktop keeps the centered layout.
    <div className="fixed inset-0 z-50 flex justify-center items-start sm:items-center pt-[calc(env(safe-area-inset-top)+4rem)] sm:pt-4 px-4 pb-[calc(env(safe-area-inset-bottom)+2.5rem)] sm:pb-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-card border border-border rounded-xl shadow-xl max-h-full sm:max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            <h2 className="text-lg font-semibold">Notification Preferences</h2>
            {notifSaved && (
              <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400 flex items-center gap-1">
                <Check className="w-3 h-3" />
                Saved
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-secondary transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto space-y-6">
          {pushSupported && (
            <div className="rounded-lg border border-border bg-card overflow-hidden">
              <div className="flex items-center justify-between gap-3 p-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 rounded-lg bg-secondary shrink-0">
                    <Smartphone className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">Push Notifications</div>
                    <p className="text-xs text-muted-foreground">
                      {pushLoading
                        ? 'Connecting…'
                        : pushNeedsInstall
                          ? 'Install the app first to enable push notifications'
                          : pushPermission === 'denied'
                            ? (isIOS()
                                ? 'Notifications blocked — re-enable in iPhone Settings → Notifications → Code Concierge'
                                : "Notifications blocked — re-enable in your browser's site settings (click the lock icon in the address bar)")
                            : 'Get notified about builds, shares, and more'}
                    </p>
                    {pushError && (
                      <p className="text-xs text-destructive mt-1 font-medium">{pushError}</p>
                    )}
                  </div>
                </div>
                <Switch
                  checked={pushEnabled}
                  onCheckedChange={(checked) => checked ? enablePush() : disablePush()}
                  disabled={pushLoading || pushNeedsInstall || pushPermission === 'denied'}
                />
              </div>
            </div>
          )}

          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-3">Platform</h4>
            <div className="space-y-1">
              {PLATFORM_NOTIFICATION_TYPES.map(({ type, label, description }) => (
                <label key={type} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-secondary/50 transition-colors cursor-pointer">
                  <div className="flex flex-col gap-0.5 min-w-0 pr-3">
                    <span className="text-sm">{label}</span>
                    {description && (
                      <span className="text-xs text-muted-foreground">{description}</span>
                    )}
                  </div>
                  <input
                    type="checkbox"
                    checked={notifPrefs[type] !== false}
                    onChange={(e) => handleNotifToggle(type, e.target.checked)}
                    disabled={notifSaving}
                    className="w-4 h-4 rounded border-border"
                  />
                </label>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-3">Your Apps</h4>
            <div className="space-y-1">
              {USER_NOTIFICATION_TYPES.map(({ type, label, highPriority }) => (
                <label key={type} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-secondary/50 transition-colors cursor-pointer">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{label}</span>
                    {highPriority && (
                      <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-500">
                        <AlertTriangle className="w-3 h-3" />
                        High priority
                      </span>
                    )}
                  </div>
                  <input
                    type="checkbox"
                    checked={notifPrefs[type] !== false}
                    onChange={(e) => handleNotifToggle(type, e.target.checked)}
                    disabled={notifSaving}
                    className="w-4 h-4 rounded border-border"
                  />
                </label>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-3">Claude Workspace</h4>
            <div className="space-y-1">
              {CLAUDE_NOTIFICATION_TYPES.map(({ type, label, highPriority }) => (
                <label key={type} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-secondary/50 transition-colors cursor-pointer">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{label}</span>
                    {highPriority && (
                      <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-500">
                        <AlertTriangle className="w-3 h-3" />
                        High priority
                      </span>
                    )}
                  </div>
                  <input
                    type="checkbox"
                    checked={notifPrefs[type] !== false}
                    onChange={(e) => handleNotifToggle(type, e.target.checked)}
                    disabled={notifSaving}
                    className="w-4 h-4 rounded border-border"
                  />
                </label>
              ))}
            </div>
          </div>

          {user.isAdmin && (
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                Admin
                <Lock className="w-3 h-3" />
              </h4>
              <div className="space-y-1">
                {ADMIN_NOTIFICATION_TYPES.map(({ type, label, highPriority }) => (
                  <label key={type} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-secondary/50 transition-colors cursor-pointer">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{label}</span>
                      {highPriority && (
                        <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-500">
                          <AlertTriangle className="w-3 h-3" />
                          High priority
                        </span>
                      )}
                    </div>
                    <input
                      type="checkbox"
                      checked={notifPrefs[type] !== false}
                      onChange={(e) => handleNotifToggle(type, e.target.checked)}
                      disabled={notifSaving}
                      className="w-4 h-4 rounded border-border"
                    />
                  </label>
                ))}
              </div>

              <h5 className="text-xs font-medium text-muted-foreground mt-4 mb-2 uppercase tracking-wider">Platform Monitoring</h5>
              <div className="space-y-1">
                {ADMIN_MONITORING_TYPES.map(({ type, label, description, highPriority }) => (
                  <label key={type} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-secondary/50 transition-colors cursor-pointer">
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{label}</span>
                        {highPriority && (
                          <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-500">
                            <AlertTriangle className="w-3 h-3" />
                            High priority
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">{description}</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={notifPrefs[type] === true}
                      onChange={(e) => handleNotifToggle(type, e.target.checked)}
                      disabled={notifSaving}
                      className="w-4 h-4 rounded border-border"
                    />
                  </label>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Changes are saved automatically. These preferences also apply in Settings.
          </p>
        </div>
      </div>
    </div>
  )
}
